import { Controller } from '@hotwired/stimulus'
import { useRecaptcha } from '../mixins/useRecaptcha'

// Imports a job from an external URL and auto-fills the new-job form.
// POSTs the URL to `endpointValue`, receives a polling token, then polls
// `${endpointValue}/${token}` until the extraction is ready, maps the returned
// `fields` (keyed by input `name`) onto the form, and badges low-confidence
// fields with a "please verify" note.
export default class extends Controller {
  static targets = ['url', 'button', 'label', 'spinner', 'feedback', 'companyCta', 'companyName', 'form']
  static values = {
    endpoint: String,
    submitText: String,
    loadingText: String,
    successText: String,
    failedText: String,
    invalidText: String,
    verifyText: String
  }

  static classes = ['filled', 'invalid']

  POLL_INTERVAL = 1500
  POLL_TIMEOUT = 40000
  RUN_TIMEOUT = 45000

  connect () {
    useRecaptcha(this)
  }

  submit () {
    const url = this.urlTarget.value.trim()

    if (!this.#looksLikeUrl(url)) {
      this.#showFeedback('danger', this.invalidTextValue)
      this.urlTarget.classList.add(this.invalidClass)
      return
    }

    this.#run(url)
  }

  clearInvalid () {
    this.urlTarget.classList.remove(this.invalidClass)
    this.#hideFeedback()
  }

  submitOnEnter (event) {
    if (event.key !== 'Enter') return

    event.preventDefault()
    this.submit()
  }

  async #run (url) {
    this.#setLoading(true)

    try {
      await this.#withTimeout(this.#extractAndFill(url), this.RUN_TIMEOUT)
    } catch (error) {
      this.#showFeedback('danger', this.failedTextValue)
    } finally {
      this.#setLoading(false)
    }
  }

  async #extractAndFill (url) {
    const start = await this.#start(url)

    if (!start.ok) {
      this.#showFeedback('danger', start.error || this.failedTextValue)
      return
    }

    const result = await this.#poll(start.token)

    if (result.status === 'ready' && Object.keys(result.fields || {}).length > 0) {
      // Reveal the form BEFORE filling so widgets that need layout — the
      // TomSelect specialization multi-select — apply their values while visible.
      if (this.hasFormTarget) this.formTarget.classList.remove('d-none')
      this.#fillForm(result.fields)
      await this.#fillLocation(result.fields)
      this.#fillSpecializations(result.specialization_ids)
      this.#prefillCompany(result.company)
      this.#markVerify(result)
      this.#showFeedback('success', this.successTextValue)
    } else {
      this.#showFeedback('danger', this.failedTextValue)
    }
  }

  // Hard cap on the whole run: if the work stalls anywhere (a hung dynamic
  // import, a never-settling fetch), reject so #run's catch/finally reset the
  // UI instead of spinning forever. The abandoned work promise stays pending,
  // which is harmless.
  #withTimeout (promise, ms) {
    return Promise.race([
      promise,
      new Promise((resolve, reject) => setTimeout(() => reject(new Error('autofill timeout')), ms))
    ])
  }

  async #start (url) {
    // reCAPTCHA v3 token for the anti-abuse gate. If it can't be obtained
    // (offline/dev), send empty and let the server decide (dev skips the check).
    let recaptchaToken = ''
    try {
      recaptchaToken = await this.recaptcha.getToken('parse_url')
    } catch (error) {
      // ignore — proceed with an empty token
    }

    const response = await fetch(this.endpointValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.#csrfToken(),
        Accept: 'application/json'
      },
      body: JSON.stringify({ url, 'g-recaptcha-response': recaptchaToken })
    })

    const data = await this.#safeJson(response)

    if (!response.ok) return { ok: false, error: data.error }
    if (!data.token) return { ok: false }

    return { ok: true, token: data.token }
  }

  async #poll (token) {
    const statusUrl = `${this.endpointValue}/${token}`
    const deadline = Date.now() + this.POLL_TIMEOUT

    while (Date.now() < deadline) {
      await this.#sleep(this.POLL_INTERVAL)

      try {
        const response = await fetch(statusUrl, { headers: { Accept: 'application/json' } })
        if (!response.ok) continue

        const data = await response.json()
        if (data.status === 'ready' || data.status === 'failed') return data
      } catch (error) {
        // Transient network/parse error — keep polling until the deadline.
      }
    }

    return { status: 'failed' }
  }

  async #safeJson (response) {
    try {
      return await response.json()
    } catch (error) {
      return {}
    }
  }

  #fillForm (fields) {
    const form = this.#form
    if (!form) return

    Object.entries(fields).forEach(([name, value]) => {
      // Location is a dependent country->city dropdown — handled by #fillLocation.
      if (name.startsWith('job[location_attributes]')) return

      const field = form.querySelector(`[name="${name}"]`)
      if (!field || value == null) return

      field.value = value
      field.dispatchEvent(new Event('input', { bubbles: true }))
      field.dispatchEvent(new Event('change', { bubbles: true }))
      this.#syncRichEditor(field, value)
      this.#flash(field)
    })

    form.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // The specialization field is a TomSelect multi-select, so setting `value`
  // directly (as #fillForm does for plain inputs) won't register the chips.
  // Drive it through the select-multiple controller; fall back to selecting the
  // <option>s if TomSelect hasn't mounted yet.
  #fillSpecializations (ids) {
    if (!Array.isArray(ids) || ids.length === 0) return

    const form = this.#form
    // Target the <select> specifically: SimpleForm also emits a hidden
    // <input name="job[specialization_ids][]"> (so an empty multi-select still
    // submits), and it comes first — a bare [name] selector would grab it.
    const select = form?.querySelector('select[name="job[specialization_ids][]"]')
    if (!select) return

    const controller = this.application.getControllerForElementAndIdentifier(select, 'select-multiple')
    if (controller) {
      controller.setValues(ids)
    } else {
      ids.forEach((id) => {
        const option = select.querySelector(`option[value="${id}"]`)
        if (option) option.selected = true
      })
      select.dispatchEvent(new Event('change', { bubbles: true }))
    }
    this.#flash(select)
  }

  // Country -> city is a dependent dropdown: changing the country triggers an async
  // fetch (location-controller) that REPLACES the city <select>'s options. So set the
  // country first, wait until the fetched options include our target city, then select
  // it — otherwise the value is applied against stale options and wiped by the refresh.
  async #fillLocation (fields) {
    const form = this.#form
    if (!form) return

    const countryId = fields['job[location_attributes][country_id]']
    if (countryId == null) return

    const countrySelect = form.querySelector('select[name="job[location_attributes][country_id]"]')
    if (!countrySelect) return

    countrySelect.value = String(countryId)
    countrySelect.dispatchEvent(new Event('change', { bubbles: true }))
    this.#flash(countrySelect)

    const cityId = fields['job[location_attributes][city_id]']
    if (cityId == null) return

    const citySelect = form.querySelector('select[name="job[location_attributes][city_id]"]')
    if (!citySelect) return

    const ready = await this.#waitFor(() => citySelect.querySelector(`option[value="${cityId}"]`) != null)
    if (!ready) return

    citySelect.value = String(cityId)
    citySelect.dispatchEvent(new Event('change', { bubbles: true }))
    this.#flash(citySelect)
  }

  async #waitFor (predicate, timeout = 3000, interval = 100) {
    const deadline = Date.now() + timeout
    while (Date.now() < deadline) {
      if (predicate()) return true
      await this.#sleep(interval)
    }
    return predicate()
  }

  // When the parse found a company that doesn't match an existing one, prefill the
  // "Add company" modal and reveal a CTA so the USER reviews and creates it
  // (a complete, confirmed company). No silent draft, no admin step.
  #prefillCompany (company) {
    if (!company || !company.name) return

    const modal = document.getElementById('newCompanyModal')
    if (modal) {
      const set = (name, value) => {
        const el = modal.querySelector(`[name="${name}"]`)
        if (el && value != null) {
          el.value = value
          el.dispatchEvent(new Event('input', { bubbles: true }))
          el.dispatchEvent(new Event('change', { bubbles: true }))
        }
      }
      set('company[name]', company.name)
      set('company[external_url]', company.website)
      set('company[employees_size]', company.size)
      set('company[logo_url]', company.logo_url)
    }

    if (this.hasCompanyCtaTarget) {
      if (this.hasCompanyNameTarget) this.companyNameTarget.textContent = company.name
      this.companyCtaTarget.classList.remove('d-none')
    }
  }

  // Salary must always carry the verify note when the AI returned a salary,
  // independent of the backend's confidence signal. Other fields keep their
  // low-confidence behavior.
  #markVerify (result) {
    const lowConfidence = result.low_confidence || []
    const fields = result.fields || {}
    // Both `fields` and `low_confidence` are keyed by INPUT name, not by the
    // extractor's field name (the server maps them through FIELD_TO_INPUT), so
    // these have to be the bracketed form or the note silently never appears.
    const autofilledSalary = ['job[salary_from]', 'job[salary_to]'].filter((name) => fields[name] != null)
    this.#markLowConfidence([...new Set([...lowConfidence, ...autofilledSalary])])
  }

  #markLowConfidence (names) {
    const form = this.#form
    if (!form) return

    form.querySelectorAll('.url-import__verify').forEach((note) => note.remove())

    const noted = new Set()
    names.forEach((name) => {
      const field = form.querySelector(`[name="${name}"]`)
      if (!field) return

      const container = field.closest('.mb-4') || field.parentElement
      if (!container || noted.has(container)) return
      noted.add(container)

      const note = document.createElement('small')
      note.className = 'url-import__verify'
      note.textContent = this.verifyTextValue
      container.appendChild(note)
    })
  }

  // The description field is backed by a TinyMCE instance, so the textarea
  // value alone won't show up — push the content into the live editor too.
  #syncRichEditor (field, value) {
    if (!field.id || !window.tinymce) return

    window.tinymce.get(field.id)?.setContent(String(value))
  }

  #flash (field) {
    const target = field.closest('.mb-4') || field
    target.classList.remove(this.filledClass)
    // Force reflow so the animation restarts on repeated imports.
    // eslint-disable-next-line no-void
    void target.offsetWidth
    target.classList.add(this.filledClass)
    target.addEventListener(
      'animationend',
      () => target.classList.remove(this.filledClass),
      { once: true }
    )
  }

  #setLoading (loading) {
    this.buttonTarget.disabled = loading
    this.urlTarget.disabled = loading

    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle('d-none', !loading)
    }

    if (this.hasLabelTarget) {
      this.labelTarget.textContent = loading ? this.loadingTextValue : this.submitTextValue
    }
  }

  #showFeedback (variant, message) {
    if (!this.hasFeedbackTarget) return

    this.feedbackTarget.className = `alert alert-${variant} mt-3 mb-0`
    this.feedbackTarget.textContent = message
    this.feedbackTarget.classList.remove('d-none')
  }

  #hideFeedback () {
    if (!this.hasFeedbackTarget) return

    this.feedbackTarget.classList.add('d-none')
  }

  #sleep (ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  #looksLikeUrl (value) {
    return /^(https?:\/\/)?[^\s.]+\.[^\s]{2,}/i.test(value)
  }

  #csrfToken () {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  get #form () {
    if (this.hasFormTarget) return this.formTarget.querySelector('form')
    // Legacy single-panel layout (removed with the old combined view in Task 4).
    return this.element.closest('[data-job-mode-target="singlePanel"]')?.querySelector('form') ||
      this.element.closest('.container')?.querySelector('form')
  }
}
